import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createSlots as _createSlots } from "vue"

const _hoisted_1 = { role: "timer" }

import { useFocus } from '@vueuse/core';
import { ref, computed, toRefs, watch } from 'vue';

// Vendor: vue-json-pretty
import 'vue-json-pretty/lib/styles.css';
import VueJsonPretty from 'vue-json-pretty';

// PERSONAL COMPONENTS
import QBtnValid from '@components/form/QBtnValid.vue';
import QDialogTemplate from '@components/template/QDialogTemplate.vue';
import QCustomInput from '@components/form/QCustomInput.vue';

// Stores
import { useUiStore } from '@stores/ui';
import { useSecurityStore } from '@stores/security';

// ViewModel
import { useGlobalVM } from '@viewmodel/global.vm';

// PERSONAL LIBS
import { IConfig } from '@shared/interface/config.interface';
import { IUser } from '@shared/interface/model.interface';
import {
  displayNotifSuccess,
  displayDialogError,
} from '@shared/lib/notify.lib';
import { RoleEnum } from '@shared/enum/role.enum';
import userRepository from '@data/repository/user.repository';
import {
  convertBackendDateToDate,
  convertDateToLiterral,
  convertSecondsInFinestTime,
} from '@shared/lib/date.lib';
import { $t } from '@i18n/utils';
import { IDataTime } from '@shared/interface/cronjob.interface';
import {
  passwordExample,
} from '@shared/lib/form.lib';
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';
import { SecretTypeEnum } from '@shared/enum/secret-type.enum'
import { InputFiedTypeEnum } from '@shared/enum/input.enum'
import { FormNameEnum } from '@shared/enum/form-name.enum';

/**
 * Events
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'DialogUserAccount',
  props: {
  show: { type: Boolean, required: true, default: false },
},
  emits: ['close', 'success', 'failed'],
  setup(__props, { emit: __emit }) {

// VENDOR
const emits = __emit;

/**
 * Stores
 */
const uiStore = useUiStore();
const securityStore = useSecurityStore();

/**
 * Viewmodel
 */
const { isMobile } = useGlobalVM();

/**
 * HTMLElement ref
 */
const qInputCurrentPasswordRef = ref();
const qInputNewPasswordRef = ref();
const qInputNewPasswordConfirmRef = ref();
const qInputUsernameRef = ref();
const { focused: qInputUsernameRefFocused } = useFocus(qInputUsernameRef);

/**
 * Reactive datas
 */
const tab = ref('password');
const loading = ref<boolean>(false);
const username = ref<string | undefined>(undefined);
const currentPassword = ref<string | undefined>(undefined);
const newPassword = ref<string | undefined>(undefined);
const newPasswordConfirm = ref<string | undefined>(undefined);

/**
 * Computed properties
 */
const activeUser = computed<IUser | undefined>(
  () => sessionManager.currentUser.value,
);
const refreshTokenRemainingTime = computed<IDataTime | undefined>(
    () => sessionManager.refreshToken.remainingDataTime.value,
);
const refreshTokenRemainingTimeStr = computed<string>(() =>
  convertSecondsInFinestTime(refreshTokenRemainingTime.value, true),
);
const config = computed<IConfig | undefined>(() => uiStore.config);
const connectedUser = computed<IUser | undefined>(
  () => sessionManager.currentUser.value,
);
const isSuperAdmin = computed<boolean>(
  () =>
    connectedUser.value?.roles?.includes(RoleEnum.ROLE_SUPER_ADMIN) === true,
);
const isDev = computed<boolean>(
  () => connectedUser.value?.roles?.includes(RoleEnum.ROLE_DEV) === true,
);
const lastConnection = computed<string>(() => {
  if (connectedUser.value?.lastConnectionAt != null) {
    return `Dernière connexion le ${convertDateToLiterral(
      convertBackendDateToDate(connectedUser.value.lastConnectionAt),
    )}`;
  } else {
    return 'Jamais connecté';
  }
});

/**
 * Properties
 */
const props = __props;
const { show } = toRefs(props);

/**
 * Methods
 */
function onClose() {
  emits('close');
}

function clearUsername() {
  qInputUsernameRef.value?.resetValidation();
  username.value = undefined;
  qInputUsernameRefFocused.value = true;
}

function validUsername() {
    // Reset focus
    qInputUsernameRef.value?.blur()

    // Validate
    qInputUsernameRef.value?.validate()

    if (qInputUsernameRef.value != null && qInputUsernameRef.value.hasError()) {
      qInputUsernameRef.value.focus()
      return false;
    }

    return true;
}

async function onValidUsername() {
  if (!validUsername()) {
    return;
  }

  loading.value = true;

  userRepository
    .updateUsername({
      username: username.value ?? '',
      csrf: (await securityStore.getCsrf(FormNameEnum.ACCOUNT_USERNAME))?.token
    })
    .then(() => {
      displayNotifSuccess("Nom d'utilisateur mis à jour avec succès !");
    })
    .catch((error) => {
      displayDialogError({ message: error.message ?? 'null' });
    })
    .finally(() => {
      loading.value = false;
      clearUsername();
    });
}

function validPassword() {
    // Reset focus
    qInputCurrentPasswordRef.value?.blur();
    qInputNewPasswordRef.value?.blur();
    qInputNewPasswordConfirmRef.value?.blur();

    // Validate
    qInputCurrentPasswordRef.value?.validate();
    qInputNewPasswordRef.value?.validate();
    qInputNewPasswordConfirmRef.value?.validate();

    if (qInputCurrentPasswordRef.value != null && qInputCurrentPasswordRef.value.hasError()) {
      qInputCurrentPasswordRef.value.focus()
      return false;
    }

    if (qInputNewPasswordRef.value != null && qInputNewPasswordRef.value.hasError()) {
      qInputNewPasswordRef.value.focus()
      return false;
    }

    if (qInputNewPasswordConfirmRef.value != null && qInputNewPasswordConfirmRef.value.hasError()) {
      qInputNewPasswordConfirmRef.value.focus()
      return false;
    }

    if (newPassword.value !== newPasswordConfirm.value) {
      displayDialogError({
        message:
          'Le nouveau mot de passe et sa confirmation doivent être strictement identiques.',
      });
      return false;
    }

    return true;
}

async function onValidPassword() {
  if (!validPassword()) {
    return;
  }

  loading.value = true;

  userRepository
    .updatePassword({
      currentPassword: currentPassword.value ?? '',
      newPassword: newPassword.value ?? '',
      newPasswordConfirm: newPasswordConfirm.value ?? '',
      csrf: (await securityStore.getCsrf(FormNameEnum.ACCOUNT_PASSWORD))?.token
    })
    .then(() => {
      displayNotifSuccess('Mot de passe mis à jour avec succès !');
    })
    .catch((error) => {
      displayDialogError({
        message: error.message ?? 'null',
      });
    })
    .finally(() => {
      loading.value = false;
      clearPasswordInputs();
    });
}

function clearPasswordInputs() {
  qInputCurrentPasswordRef.value?.resetValidation();
  qInputNewPasswordRef.value?.resetValidation();
  qInputNewPasswordConfirmRef.value?.resetValidation();

  currentPassword.value = undefined;
  newPassword.value = undefined;
  newPasswordConfirm.value = undefined;
}

/**
 * Watch
 */
watch(
  () => show.value,
  (newVal) => {
    if (newVal === true) {
      clearPasswordInputs();
      username.value = undefined;
    }
  },
  { immediate: true },
);

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_tab = _resolveComponent("q-tab")!
  const _component_q_tabs = _resolveComponent("q-tabs")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_tab_panel = _resolveComponent("q-tab-panel")!
  const _component_q_tab_panels = _resolveComponent("q-tab-panels")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!

  return (_openBlock(), _createBlock(QDialogTemplate, {
    "show-dialog": _unref(show),
    title: 'Mon compte',
    maximized: _unref(isMobile),
    persistent: loading.value,
    "disable-close-button": loading.value,
    onClose: _cache[9] || (_cache[9] = ($event: any) => (onClose()))
  }, _createSlots({
    content: _withCtx(() => [
      (connectedUser.value != null)
        ? (_openBlock(), _createBlock(_component_q_card, {
            key: 0,
            bordered: "",
            class: "q-my-xs q-mx-sm no-border-radius"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_q_card_section, { class: "q-py-xs" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(connectedUser.value.username) + " (" + _toDisplayString(connectedUser.value.email) + ") ", 1 /* TEXT */)
                ]),
                _: 1 /* STABLE */
              }),
              (isDev.value)
                ? (_openBlock(), _createBlock(_component_q_card_section, { key: 0 }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(VueJsonPretty), {
                        data: 
              activeUser.value ? JSON.parse(JSON.stringify(activeUser.value)) : undefined
            ,
                        showLength: true,
                        showSelectController: true,
                        highlightSelectedNode: true,
                        collapsedNodeLength: 1
                      }, null, 8 /* PROPS */, ["data"])
                    ]),
                    _: 1 /* STABLE */
                  }))
                : _createCommentVNode("v-if", true)
            ]),
            _: 1 /* STABLE */
          }))
        : _createCommentVNode("v-if", true),
      (connectedUser.value != null)
        ? (_openBlock(), _createBlock(_component_q_card, {
            key: 1,
            bordered: "",
            class: "q-my-xs q-mx-sm"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_q_card_section, { class: "q-py-xs" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(lastConnection.value), 1 /* TEXT */)
                ]),
                _: 1 /* STABLE */
              })
            ]),
            _: 1 /* STABLE */
          }))
        : _createCommentVNode("v-if", true),
      (
          isSuperAdmin.value &&
          _unref(isMobile)
        )
        ? (_openBlock(), _createBlock(_component_q_card, {
            key: 2,
            bordered: "",
            class: "q-my-xs q-mx-sm"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_q_card_section, { class: "q-py-xs" }, {
                default: _withCtx(() => [
                  _createElementVNode("div", null, [
                    _createElementVNode("div", null, [
                      _cache[10] || (_cache[10] = _createTextVNode(" Session (temps restant) : ")),
                      _createElementVNode("span", _hoisted_1, _toDisplayString(refreshTokenRemainingTimeStr.value), 1 /* TEXT */)
                    ])
                  ])
                ]),
                _: 1 /* STABLE */
              })
            ]),
            _: 1 /* STABLE */
          }))
        : _createCommentVNode("v-if", true),
      _createVNode(_component_q_card, {
        bordered: "",
        class: "q-my-xs q-mx-sm"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_q_tabs, {
            modelValue: tab.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((tab).value = $event)),
            align: "justify",
            class: "shadow-2"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_q_tab, {
                name: "password",
                icon: "lock",
                label: "Mot de passe"
              }),
              _createVNode(_component_q_tab, {
                name: "username",
                icon: "person",
                label: "Nom d'utilisateur"
              })
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["modelValue"]),
          _createVNode(_component_q_separator, {
            role: "separator",
            "aria-hidden": "true"
          }),
          _createVNode(_component_q_tab_panels, {
            modelValue: tab.value,
            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((tab).value = $event)),
            animated: ""
          }, {
            default: _withCtx(() => [
              _createVNode(_component_q_tab_panel, {
                name: "password",
                id: "password_description"
              }, {
                default: _withCtx(() => [
                  _cache[12] || (_cache[12] = _createElementVNode("h3", { class: "sr-only" }, "Modification du mot de passe", -1 /* HOISTED */)),
                  _createVNode(_component_q_card_section, { class: "text-caption q-my-none q-px-none q-pt-none q-pb-xs" }, {
                    default: _withCtx(() => _cache[11] || (_cache[11] = [
                      _createTextVNode(" * : champs obligatoires ")
                    ])),
                    _: 1 /* STABLE */
                  }),
                  _createVNode(_component_q_card_section, {
                    class: "q-pa-none",
                    role: "group",
                    "aria-label": "Modification du mot de passe : saisie du mot de passe actuel, puis du nouveau mot de passe et enfin de la confirmation du nouveau mot de passe"
                  }, {
                    default: _withCtx(() => [
                      _createCommentVNode(" Input : current password "),
                      _createVNode(QCustomInput, {
                        modelValue: currentPassword.value,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((currentPassword).value = $event)),
                        ref_key: "qInputCurrentPasswordRef",
                        ref: qInputCurrentPasswordRef,
                        dense: _unref(isMobile),
                        disable: loading.value,
                        "on-enter-key": onValidPassword,
                        "input-type": _unref(InputFiedTypeEnum).PASSWORD,
                        "secret-type": _unref(SecretTypeEnum).CURRENT,
                        required: true
                      }, null, 8 /* PROPS */, ["modelValue", "dense", "disable", "input-type", "secret-type"]),
                      _createVNode(_component_q_separator, {
                        class: "q-my-xs bg-transparent",
                        role: "separator",
                        "aria-hidden": "true"
                      }),
                      _createCommentVNode(" Input : new password "),
                      _createVNode(QCustomInput, {
                        modelValue: newPassword.value,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((newPassword).value = $event)),
                        ref_key: "qInputNewPasswordRef",
                        ref: qInputNewPasswordRef,
                        dense: _unref(isMobile),
                        disable: loading.value,
                        "on-enter-key": onValidPassword,
                        "input-type": _unref(InputFiedTypeEnum).PASSWORD,
                        "secret-type": _unref(SecretTypeEnum).NEW,
                        "enable-pass-checker": true,
                        required: true
                      }, null, 8 /* PROPS */, ["modelValue", "dense", "disable", "input-type", "secret-type"]),
                      _createVNode(_component_q_separator, {
                        class: "q-my-xs bg-transparent",
                        role: "separator",
                        "aria-hidden": "true"
                      }),
                      _createCommentVNode(" Input : new password confirm "),
                      _createVNode(QCustomInput, {
                        modelValue: newPasswordConfirm.value,
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((newPasswordConfirm).value = $event)),
                        ref_key: "qInputNewPasswordConfirmRef",
                        ref: qInputNewPasswordConfirmRef,
                        dense: _unref(isMobile),
                        disable: loading.value,
                        "on-enter-key": onValidPassword,
                        "input-type": _unref(InputFiedTypeEnum).PASSWORD,
                        "secret-type": _unref(SecretTypeEnum).CONFIRMATION,
                        required: true
                      }, null, 8 /* PROPS */, ["modelValue", "dense", "disable", "input-type", "secret-type"])
                    ]),
                    _: 1 /* STABLE */
                  }),
                  _createVNode(_component_q_card_section, { class: "q-px-none" }, {
                    default: _withCtx(() => [
                      _createVNode(QBtnValid, {
                        label: 'Modifier le mot de passe',
                        onClick: _cache[4] || (_cache[4] = ($event: any) => (onValidPassword())),
                        disable: loading.value,
                        loading: loading.value,
                        "loading-label": ''
                      }, null, 8 /* PROPS */, ["disable", "loading"])
                    ]),
                    _: 1 /* STABLE */
                  })
                ]),
                _: 1 /* STABLE */
              }),
              _createVNode(_component_q_tab_panel, { name: "username" }, {
                default: _withCtx(() => [
                  _createVNode(_component_q_card_section, { class: "q-pa-none" }, {
                    default: _withCtx(() => [
                      _createVNode(QCustomInput, {
                        modelValue: username.value,
                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((username).value = $event)),
                        ref_key: "qInputUsernameRef",
                        ref: qInputUsernameRef,
                        dense: _unref(isMobile),
                        disable: loading.value,
                        "on-enter-key": onValidUsername,
                        "input-type": _unref(InputFiedTypeEnum).USERNAME,
                        required: false
                      }, null, 8 /* PROPS */, ["modelValue", "dense", "disable", "input-type"])
                    ]),
                    _: 1 /* STABLE */
                  }),
                  _createVNode(_component_q_card_section, { class: "q-px-none" }, {
                    default: _withCtx(() => [
                      _createVNode(QBtnValid, {
                        label: 'Modifier le nom d\'utilisateur',
                        onClick: _cache[6] || (_cache[6] = ($event: any) => (onValidUsername())),
                        disable: loading.value,
                        loading: loading.value,
                        "loading-label": ''
                      }, null, 8 /* PROPS */, ["disable", "loading"])
                    ]),
                    _: 1 /* STABLE */
                  })
                ]),
                _: 1 /* STABLE */
              })
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["modelValue"])
        ]),
        _: 1 /* STABLE */
      })
    ]),
    footer: _withCtx(() => [
      _createVNode(_component_q_card, {
        flat: "",
        class: "no-border-radius"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_q_separator),
          _createVNode(_component_q_card_actions, { align: "right" }, {
            default: _withCtx(() => [
              (!_unref(isMobile))
                ? (_openBlock(), _createBlock(QBtnValid, {
                    key: 0,
                    label: 'Fermer',
                    onClick: _cache[8] || (_cache[8] = ($event: any) => (onClose())),
                    disable: loading.value
                  }, null, 8 /* PROPS */, ["disable"]))
                : _createCommentVNode("v-if", true)
            ]),
            _: 1 /* STABLE */
          })
        ]),
        _: 1 /* STABLE */
      })
    ]),
    _: 2 /* DYNAMIC */
  }, [
    (isSuperAdmin.value && !_unref(isMobile))
      ? {
          name: "header",
          fn: _withCtx(() => [
            _createElementVNode("div", null, [
              _createElementVNode("div", null, "Session (temps restant) : " + _toDisplayString(refreshTokenRemainingTimeStr.value), 1 /* TEXT */)
            ])
          ]),
          key: "0"
        }
      : undefined
  ]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["show-dialog", "maximized", "persistent", "disable-close-button"]))
}
}

})